import type { RefObject } from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { toYMD } from './calendar.js';

// Hook + pure time-slot helpers for the datetime picker family.
// Ported from bookcars-2.0 `use-datetime-picker.ts`; extended with a configurable
// `stepMinutes` and an `allowedWindow` (Gap-1-ready, unset by default) and typed throughout.

const MONTH_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

export const DEFAULT_STEP_MINUTES = 30;

/**
 * A time-of-day window; start/end are inclusive "HH:mm" strings in Asia/Kuala_Lumpur (MYT) wall-
 * clock time (284, #334 — was UTC; the platform has no other market, and every configured window
 * already means local hours, not UTC).
 */
export interface AllowedWindow {
  start: string;
  end: string;
}

/**
 * Malaysia has a fixed UTC+8 offset year-round (no daylight saving), so a Malaysia wall-clock
 * minutes-of-day can be derived by shifting the instant +8h and reading UTC getters — exact,
 * deterministic, and independent of the runtime's own `TZ` or a visitor's browser timezone.
 * Mirrors `backend/src/modules/pricing/tz.ts`'s `mytParts` exactly (there is no shared module
 * between this app and the backend, so this is a deliberate, small reimplementation, not drift).
 */
const MYT_OFFSET_MINUTES = 8 * 60;

function mytMinutesOfDay(date: Date): number {
  const shifted = new Date(date.getTime() + MYT_OFFSET_MINUTES * 60_000);
  return shifted.getUTCHours() * 60 + shifted.getUTCMinutes();
}

export interface TimeSlot {
  hour: number;
  minute: number;
  /** "HH:mm". */
  label: string;
  disabled: boolean;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function toMinutesOfDay(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

export function formatDisplay(date: Date | null | undefined): string {
  if (!date) return '';
  return `${pad2(date.getDate())} ${MONTH_SHORT[date.getMonth()]} ${date.getFullYear()}, ${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

/** Snap `now` up to the next step boundary (default 30 min), rolling the day if needed. */
export function computeNextSlot(
  now: Date,
  stepMinutes: number = DEFAULT_STEP_MINUTES,
): { hour: number; minute: number; dayOffset: number } {
  const mins = now.getHours() * 60 + now.getMinutes();
  const snapped = Math.ceil(mins / stepMinutes) * stepMinutes;
  const dayOffset = snapped >= 24 * 60 ? 1 : 0;
  const wrapped = snapped % (24 * 60);
  return { hour: Math.floor(wrapped / 60), minute: wrapped % 60, dayOffset };
}

/** All selectable {hour, minute} slots for a day at the given step. */
export function buildTimeSlots(stepMinutes: number = DEFAULT_STEP_MINUTES): TimeSlot[] {
  const slots: TimeSlot[] = [];
  for (let m = 0; m < 24 * 60; m += stepMinutes) {
    const hour = Math.floor(m / 60);
    const minute = m % 60;
    slots.push({ hour, minute, label: `${pad2(hour)}:${pad2(minute)}`, disabled: false });
  }
  return slots;
}

/**
 * 126: whether a specific instant (not a calendar cell) is usable under the same
 * minDate/allowedWindow rules a manually-picked time is already held to — reused
 * by BookingFormPage to validate a carried-forward window, rather than
 * re-deriving the UTC-window math (`isTimeSlotDisabled` above) a second time.
 */
export function isDateWithinWindow(
  date: Date,
  ctx: { minDate?: Date; allowedWindow?: AllowedWindow | null },
): boolean {
  if (ctx.minDate && date.getTime() < ctx.minDate.getTime()) return false;
  if (ctx.allowedWindow) {
    const mytMins = mytMinutesOfDay(date);
    const start = toMinutesOfDay(ctx.allowedWindow.start);
    const end = toMinutesOfDay(ctx.allowedWindow.end);
    if (mytMins < start || mytMins > end) return false;
  }
  return true;
}

/** 205 (#292): an already-committed instant range — a real CarBlock, read-only. */
export interface OccupiedWindow {
  start: Date;
  end: Date;
}

export interface SlotContext {
  selectedYMD: string | null;
  minDate?: Date;
  maxDate?: Date;
  allowedWindow?: AllowedWindow | null;
  occupiedWindows?: OccupiedWindow[];
  /**
   * 386 (#1301): set for a RETURN time — the chosen pickup. The slot is then judged as the end of the rental
   * `[rangeStart, slot)`, not as an instant: a return across another rental is unavailable, and a return exactly at
   * its start is available. Unset for a pickup time (the instant rule below).
   */
  rangeStart?: Date;
}

/**
 * 386 (#1301): whether the half-open rental `[start, end)` overlaps any occupied window `[w.start, w.end)`. Touching
 * ends do not overlap: a rental may end exactly when the next one starts, or start exactly when one ends.
 */
export function rangeOverlapsAny(
  windows: readonly OccupiedWindow[],
  start: Date,
  end: Date,
): boolean {
  const s = start.getTime();
  const e = end.getTime();
  return windows.some((w) => w.start.getTime() < e && w.end.getTime() > s);
}

/** Whether a given time-of-day slot is unselectable for the selected day. */
/**
 * 386 (FR-018): the latest possible return after a pickup — the start of the earliest occupied window that ends after
 * the pickup (a return may end exactly there). `null` when nothing lies ahead. A window ending exactly at the pickup
 * (an extension's own rental) does not count. The return picker uses it as its `maxDate`, so every calendar day after
 * it is greyed instead of offering a day where every time would be greyed.
 */
export function latestReturnBefore(windows: readonly OccupiedWindow[], pickup: Date): Date | null {
  const p = pickup.getTime();
  let latest: number | null = null;
  for (const w of windows) {
    if (w.end.getTime() > p && (latest === null || w.start.getTime() < latest))
      latest = w.start.getTime();
  }
  return latest === null ? null : new Date(latest);
}

export function isTimeSlotDisabled(hour: number, minute: number, ctx: SlotContext): boolean {
  const mins = hour * 60 + minute;

  // 284 (#334): allowedWindow is HH:mm Malaysia time (was UTC), and the platform is Malaysia-only
  // — the slot's own `hour`/`minute` already ARE the intended Malaysia wall-clock value the
  // customer is picking (there is no other market this could mean), so this compares them to the
  // window DIRECTLY. No Date construction, no UTC round-trip, and — unlike the old UTC-basis
  // version this replaces — no dependency on the browser's own local timezone at all.
  if (ctx.allowedWindow && ctx.selectedYMD) {
    const start = toMinutesOfDay(ctx.allowedWindow.start);
    const end = toMinutesOfDay(ctx.allowedWindow.end);
    if (mins < start || mins > end) return true;
  }

  // 205 (#292): an already-committed window — half-open [start, end), so the exact instant a
  // rental ends is itself selectable again. Absolute Date objects, so a plain epoch comparison —
  // no UTC conversion needed the way allowedWindow's recurring HH:mm rule requires above.
  if (ctx.occupiedWindows?.length && ctx.selectedYMD) {
    const [y, m, d] = ctx.selectedYMD.split('-').map(Number);
    const slot = new Date(y ?? 0, (m ?? 1) - 1, d ?? 1, hour, minute, 0, 0);
    // 386 (#1301): a return time ends the rental that starts at the pickup; the whole range must be free.
    if (ctx.rangeStart) {
      if (rangeOverlapsAny(ctx.occupiedWindows, ctx.rangeStart, slot)) return true;
    } else {
      const instant = slot.getTime();
      for (const w of ctx.occupiedWindows) {
        if (instant >= w.start.getTime() && instant < w.end.getTime()) return true;
      }
    }
  }

  // Same-day time floors/ceilings from min/max date.
  if (ctx.minDate && ctx.selectedYMD === toYMD(ctx.minDate)) {
    if (mins < ctx.minDate.getHours() * 60 + ctx.minDate.getMinutes()) return true;
  }
  if (ctx.maxDate && ctx.selectedYMD === toYMD(ctx.maxDate)) {
    if (mins > ctx.maxDate.getHours() * 60 + ctx.maxDate.getMinutes()) return true;
  }

  return false;
}

export interface UseDateTimePickerOptions {
  value: Date | null;
  onChange: (date: Date) => void;
  minDate?: Date;
  maxDate?: Date;
  stepMinutes?: number;
  allowedWindow?: AllowedWindow | null;
  occupiedWindows?: OccupiedWindow[];
  /** 386 (#1301): the chosen pickup, for a return picker (see `SlotContext.rangeStart`). */
  rangeStart?: Date;
}

export interface UseDateTimePickerResult {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  panelRef: RefObject<HTMLDivElement | null>;
  triggerRef: RefObject<HTMLElement | null>;
  viewYear: number;
  viewMonth: number;
  prevMonth: () => void;
  nextMonth: () => void;
  pendingYMD: string | null;
  pendingHour: number;
  pendingMinute: number;
  setPendingYMD: (ymd: string | null) => void;
  setPendingHour: (h: number) => void;
  setPendingMinute: (m: number) => void;
  timeSlots: TimeSlot[];
  canConfirm: boolean;
  confirm: () => void;
  displayValue: string;
}

export function useDateTimePicker(options: UseDateTimePickerOptions): UseDateTimePickerResult {
  const { value, onChange, minDate, maxDate, allowedWindow, occupiedWindows, rangeStart } = options;
  const stepMinutes = options.stepMinutes ?? DEFAULT_STEP_MINUTES;
  const now = new Date();

  const [isOpen, setIsOpen] = useState(false);
  // panelRef: the popover content; triggerRef: the field that opens it.
  // Both are excluded from outside-click so interacting with them doesn't self-close.
  const panelRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  // With no value yet, anchor the visible month on minDate (if any) so bounded
  // pickers (e.g. a return picker floored at pickup+30min) open on the right month.
  const viewBase = value ?? minDate ?? now;
  const [viewYear, setViewYear] = useState(viewBase.getFullYear());
  const [viewMonth, setViewMonth] = useState(viewBase.getMonth());

  const [pendingYMD, setPendingYMD] = useState<string | null>(value ? toYMD(value) : null);
  const [pendingHour, setPendingHour] = useState<number>(
    value?.getHours() ?? computeNextSlot(now, stepMinutes).hour,
  );
  const [pendingMinute, setPendingMinute] = useState<number>(
    value ? snapMinute(value.getMinutes(), stepMinutes) : computeNextSlot(now, stepMinutes).minute,
  );

  useEffect(() => {
    if (!isOpen) return;
    function handleOutside(e: Event) {
      const target = e.target as Node;
      const insidePanel = panelRef.current?.contains(target) ?? false;
      const insideTrigger = triggerRef.current?.contains(target) ?? false;
      if (!insidePanel && !insideTrigger) setIsOpen(false);
    }
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [isOpen]);

  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewYear((y) => y - 1);
      setViewMonth(11);
    } else setViewMonth((m) => m - 1);
  };

  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewYear((y) => y + 1);
      setViewMonth(0);
    } else setViewMonth((m) => m + 1);
  };

  const open = () => {
    if (value) {
      setPendingYMD(toYMD(value));
      setPendingHour(value.getHours());
      setPendingMinute(snapMinute(value.getMinutes(), stepMinutes));
      setViewYear(value.getFullYear());
      setViewMonth(value.getMonth());
    } else {
      const slot = computeNextSlot(now, stepMinutes);
      const start = minDate ?? new Date();
      setPendingYMD(null);
      setPendingHour(slot.hour);
      setPendingMinute(slot.minute);
      setViewYear(start.getFullYear());
      setViewMonth(start.getMonth());
    }
    setIsOpen(true);
  };

  const close = () => setIsOpen(false);

  const timeSlots = useMemo(
    () =>
      buildTimeSlots(stepMinutes).map((s) => ({
        ...s,
        disabled: isTimeSlotDisabled(s.hour, s.minute, {
          selectedYMD: pendingYMD,
          minDate,
          maxDate,
          allowedWindow,
          occupiedWindows,
          rangeStart,
        }),
      })),
    [stepMinutes, pendingYMD, minDate, maxDate, allowedWindow, occupiedWindows, rangeStart],
  );

  const canConfirm =
    pendingYMD !== null &&
    !isTimeSlotDisabled(pendingHour, pendingMinute, {
      selectedYMD: pendingYMD,
      minDate,
      maxDate,
      allowedWindow,
      occupiedWindows,
      rangeStart,
    });

  const confirm = () => {
    if (!pendingYMD) return;
    const [y, m, d] = pendingYMD.split('-').map(Number);
    const date = new Date(y ?? 0, (m ?? 1) - 1, d ?? 1, pendingHour, pendingMinute, 0, 0);
    onChange(date);
    setIsOpen(false);
  };

  return {
    isOpen,
    open,
    close,
    panelRef,
    triggerRef,
    viewYear,
    viewMonth,
    prevMonth,
    nextMonth,
    pendingYMD,
    pendingHour,
    pendingMinute,
    setPendingYMD,
    setPendingHour,
    setPendingMinute,
    timeSlots,
    canConfirm,
    confirm,
    displayValue: formatDisplay(value),
  };
}

function snapMinute(minute: number, stepMinutes: number): number {
  // Floor to the step so a non-boundary value never displays a time earlier
  // than itself (round could push 45→60 and silently drop the hour).
  return Math.floor(minute / stepMinutes) * stepMinutes;
}
